import { changeTimetoDay } from './topchartsUtils';
import moment from 'moment';
import { isString } from 'lodash-es';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { formatRegionOptions } from '@intelligence/components/RegionSelector';
import { trankMarketsParamsExceptGlobal } from '@/pages/intelligence/utils/commonUtils';
import { history } from 'umi';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getDbiUrlParams, formatRangeTimePageFunc } from '@intelligence/components/Filter/util';
import type { SpecialParamsType } from '@intelligence/types/topchart';
import { DATA_SOURCE_METRICS } from '@intelligence/_config/enumeration/dataSource';
import {
  handleSensorTowerValueFilter,
  handleAppMagicValueFilter,
  handleAllGameFilter,
} from '@intelligence/utils/topchartsUtils';
import { saveFilterName } from '@/pages/intelligence/components/Filter/util';
import { toDateTypeLy, toDateTypeFilter } from '@/pages/intelligence/utils/common';
import { URL_SHORT_LINK_NAME } from '@intelligence/utils/urlPathCfg';
import { formatDateStrPlus } from '@intelligence/v3/utils/date';
import { REVENUE_SCOPE_VALUES } from '@intelligence/utils/revenueScope';

export enum FilterTimeType {
  commonRange = 'commonRange',
  sameMonth = 'sameMonth',
  rangeMonth = 'rangeMonth',
  sameDay = 'sameDay',
  rangeDay = 'rangeDay',
  rangeWeek = 'rangeWeek',
  dayWeekMonthYearRange = 'dayWeekMonthYearRange',
}
interface TimeExtend {
  start_date: string;
  end_date: string;
  time_type: string;
  granularity?: string;
  type?: string;
  value?: any;
}

export const timeFilterFormatFormValue = (rangeTimeType: FilterTimeType) => {
  if (!rangeTimeType) return {};

  const timeConfigs = {
    [FilterTimeType.sameDay]: { needSameDay: true },
    [FilterTimeType.rangeDay]: { onlyDay: true },
    [FilterTimeType.rangeMonth]: { onlyMonth: true },
    [FilterTimeType.sameMonth]: { needSameMonth: true },
    [FilterTimeType.rangeWeek]: { onlyWeek: true },
    [FilterTimeType.dayWeekMonthYearRange]: { dayWeekMonthYearRange: true },
  } as any;

  const timeConfig = timeConfigs[rangeTimeType] || {};
  return timeConfig;
};

/** 筛选器保存方案时，有一些保存的方案与本页面筛选器对应的配置不一至，
 *切换筛选筛选器保存方案时： 需要对保存方案进行过滤筛 */
export const formatFilterSaveValue = (params: {
  values: any; // 筛选器保存过来的值
  timeConfig?: {
    onlyDay?: boolean;
    onlyWeek?: boolean;
    onlyMonth?: boolean;
    needSameDay?: boolean;
    needSameMonth?: boolean;
    timeFilterKey?: string;
    dayWeekMonthYearRange?: boolean;
  };
  selectRegionConfig?: {
    isSingleCountry?: boolean;
    regionFilterKey?: string;
  };
}) => {
  const { values, timeConfig, selectRegionConfig } = params;
  const {
    needSameDay,
    needSameMonth,
    onlyDay,
    onlyWeek,
    onlyMonth,
    timeFilterKey = filterKeyMap.RANGE_TIME,
    dayWeekMonthYearRange,
  } = timeConfig || {};
  const { isSingleCountry, regionFilterKey = filterKeyMap.COMPOSED_COUNTRY_SELECT } =
    selectRegionConfig || {};

  // 创建一个新的筛选器值对象，用于后续的修改
  const newFilterValues = Object.assign({}, values);
  const timeFilter = newFilterValues?.[timeFilterKey];
  const composeRegionFilter = newFilterValues?.[regionFilterKey];

  // 只有天粒度/周粒度/月粒度时；可能存在多个粒度--可选范围
  if (onlyWeek || onlyDay || onlyMonth) {
    const weekUnit = 'week';
    const dayUnit = 'date';
    const monthUnit = 'month';
    if (
      (onlyDay && timeFilter?.type !== dayUnit) ||
      (onlyWeek && timeFilter?.type !== weekUnit) ||
      (onlyMonth && timeFilter?.type !== monthUnit)
    ) {
      delete newFilterValues[timeFilterKey];
    }
  }
  // 如果需要在同一天或同一月，根据时间条件调整筛选器的值 -- 只能选单天/月/
  if (needSameDay || needSameMonth) {
    const timeUnit = needSameMonth ? 'month' : 'day';
    const timeType = needSameMonth ? 'month' : 'date';
    const isSameTime = isSameDate(timeFilter?.value?.[0], timeFilter?.value?.[1], timeUnit);
    if (timeFilter?.type !== timeType || !isSameTime) {
      delete newFilterValues[timeFilterKey];
    }
  }
  // console.log(isSingleCountry, composeRegionFilter?.value?.countries)
  // 如果下拉筛选器类型为单选国家，根据该条件调整地区筛选器的值
  if (isSingleCountry) {
    if (composeRegionFilter?.value?.countries?.length !== 1) {
      delete newFilterValues[regionFilterKey];
    } else {
      // 如果只有一个国家，则将类型设置为single，因为可能是多国家筛选器设置的一个国家
      newFilterValues[regionFilterKey].value.type = 'single';
    }
  }

  if (dayWeekMonthYearRange) {
    return newFilterValues;
  }
  // 默认没有年
  if (timeFilter?.type === 'year') {
    delete newFilterValues[timeFilterKey];
  }
  return newFilterValues;
};

/** 对时间的继承值作特殊处理
 * 目前同一个时间筛选器相互间都会作继承，对一些特殊的配置作特殊处理
 * 如单月粒度只继承单月，月粒度只继承月，周/单周同样处理
 */
export const handleExtendTimeFilterValue = (params: {
  extendFilterValues: any;
  defaultValue: any;

  onlyDay?: boolean;
  onlyWeek?: boolean;
  onlyMonth?: boolean;
  needSameDay?: boolean;
  needSameMonth?: boolean;
  timeFilterKey?: string;
}) => {
  const {
    extendFilterValues,
    defaultValue,
    needSameDay,
    needSameMonth,
    onlyDay,
    onlyWeek,
    onlyMonth,
  } = params || {};

  // 只有天粒度/周粒度/月粒度时；可能存在多个粒度--可选范围
  if (onlyWeek || onlyDay || onlyMonth) {
    const weekUnit = 'week';
    const dayUnit = 'date';
    const monthUnit = 'month';
    if (
      (onlyDay && extendFilterValues?.time_type === dayUnit) ||
      (onlyWeek && extendFilterValues?.time_type === weekUnit) ||
      (onlyMonth && extendFilterValues?.time_type === monthUnit)
    ) {
      return extendFilterValues?.filterValue;
    }
    return defaultValue;
  }

  // 如果需要在同一天或同一月，根据时间条件调整筛选器的值 -- 只能选单天/月/
  if (needSameDay || needSameMonth) {
    const timeUnit = needSameMonth ? 'month' : 'day';
    const timeType = needSameMonth ? 'month' : 'date';
    const isSameTime = isSameDate(
      extendFilterValues?.start_date,
      extendFilterValues?.end_date,
      timeUnit,
    );

    if (extendFilterValues?.time_type === timeType && isSameTime) {
      return extendFilterValues?.filterValue;
    }
    // console.debug('extendFilterValues?.isSameTime', isSameTime, extendFilterValues?.filterValue)
    return defaultValue;
  }

  return extendFilterValues?.filterValue;
};

/** 品类筛选器参数 */
export const handleGenreFilterParams = (genreFilterParams: any) => {
  if (genreFilterParams) {
    const newParams = {
      genre: genreFilterParams?.genre,
      sub_genre: genreFilterParams?.sub_genre,
      genre_source: genreFilterParams?.genre_source,
      iegg_genre_req: genreFilterParams?.iegg_genre_req,
      appannie_genre: genreFilterParams?.appannie_genre,
      sensortower_genre: genreFilterParams?.sensortower_genre,
      iegg_genre: genreFilterParams?.iegg_genre,
      appmagic_genre: genreFilterParams?.appmagic_genre,
    };
    return newParams;
  }
  return {};
};

/** ReleaseTime筛选器参数 */
export const handleReleaseTimeFilterParams = (releaseFilterParams: any) => {
  if (releaseFilterParams) {
    const newParams = {
      start_release_time: releaseFilterParams?.start_release_time || '',
      end_release_time: releaseFilterParams?.end_release_time || '',
    };
    return newParams;
  }
  return {};
};

/** 时间筛选器参数 */
export const handleCommonTimeFilterParams = (
  timeFilterParams: any,
  rangeTimeType: FilterTimeType,
) => {
  if (timeFilterParams) {
    // 利用映射处理特殊参数条件，键是specialParams中的rangeTimeType，值是到timeExtend属性的映射
    // 如果相应的键在rangeTimeMap中并且条件成立，则timeExtend赋值给timeParams
    // 粒度之间有相同粒度才继承
    const conditions = {
      // 仅月粒度 且 单月
      [FilterTimeType.sameMonth]: ({
        start_date,
        end_date,
        time_type,
        type,
        value = [],
      }: TimeExtend) => {
        const typeValue: any = time_type || type;
        const startValue: any =
          start_date ?? (Array.isArray(value) && value?.length > 1) ? value[0] : '';
        const endValue: any =
          end_date ?? (Array.isArray(value) && value?.length > 1) ? value[1] : '';
        return typeValue === 'month' && isSameDate(startValue, endValue, 'month');
      },
      // 仅月粒度
      [FilterTimeType.rangeMonth]: ({ time_type, type }: TimeExtend) => {
        const typeValue: any = time_type ?? type;
        // 有日周月,没有年
        return toDateTypeFilter[typeValue] === 'month';
      },
      // 仅日粒度 且 单日
      [FilterTimeType.sameDay]: ({
        start_date,
        end_date,
        time_type,
        type,
        value = [],
      }: TimeExtend) => {
        const typeValue: any = time_type || type;
        const startValue: any =
          start_date ?? (Array.isArray(value) && value?.length > 1) ? value[0] : '';
        const endValue: any =
          end_date ?? (Array.isArray(value) && value?.length > 1) ? value[1] : '';
        return typeValue === 'date' && isSameDate(startValue, endValue, 'day');
      },
      // 仅日粒度
      [FilterTimeType.rangeDay]: ({ time_type, type }: TimeExtend) => {
        const typeValue: any = time_type ?? type;
        // 有日周月,没有年
        return toDateTypeFilter[typeValue] === 'date';
      },
      // 仅周粒度
      [FilterTimeType.rangeWeek]: ({ time_type, type }: TimeExtend) => {
        const typeValue: any = time_type ?? type;
        // 有日周月,没有年
        return toDateTypeFilter[typeValue] === 'week';
      },
      // 日，周，月粒度
      [FilterTimeType.commonRange]: ({ time_type, type }: TimeExtend) => {
        const typeValue: any = time_type ?? type;
        // 有日周月,没有年
        return toDateTypeFilter[typeValue] !== 'year';
      },
      // 日，周，月粒度 + 年粒度
      [FilterTimeType.dayWeekMonthYearRange]: () => true,
    };

    // 当rangeTimeType不存在于conditions中，或者相应条件不满足时，timeParams默认为timeExtend
    const timeParams = conditions?.[rangeTimeType]?.(timeFilterParams) ? timeFilterParams : {};

    const startDate =
      Array.isArray(timeFilterParams?.value) && timeFilterParams?.value?.length > 1
        ? formatDateStrPlus(timeFilterParams?.value[0])
        : '';
    const endDate =
      Array.isArray(timeFilterParams?.value) && timeFilterParams?.value?.length > 1
        ? formatDateStrPlus(timeFilterParams?.value[1])
        : '';

    // 如果timeParams对象不为空，则将其中的一部分属性添加到newFetchParams对象中
    if (Object.keys(timeParams).length) {
      return {
        filterValues: timeFilterParams,
        filterParams: {
          date_type: timeFilterParams?.granularity || toDateTypeLy[timeFilterParams?.type] || '',
          start_date: timeFilterParams?.start_date || startDate || '',
          end_date: timeFilterParams?.end_date || endDate || '',
        },
      };
    }
    const resolveType = (rangeTimeType: string) => {
      switch (rangeTimeType) {
        case FilterTimeType.sameMonth:
        case FilterTimeType.rangeMonth:
          return 'month';
        case FilterTimeType.rangeWeek:
          return 'week';
        default:
          return 'date';
      }
    };

    return {
      filterParams: {},
      filterValues: formatRangeTimePageFunc({
        type: resolveType(rangeTimeType),
        value: [],
      }),
    };
  }
  return {
    filterParams: {},
    filterValues: undefined,
  };
};
/** 对国家参数作交集过滤处理
 * 国家筛选器继承或取保存的值时取与目前筛选有的值的交集
 * 重新组装国家筛选器需要的数据
 * 1 接口调用时需要过滤掉不存在的值后再返回存在的值
 * 2 confirm时修处理国家参数
 *  */
export const getCountryCodeIntersection = (params: {
  markets: string[];
  countriesMarkets: string[];
  disableNoChina?: boolean;
  disableGlobal?: boolean;
  defaultMarkets: string[];
}) => {
  const { markets, countriesMarkets, disableGlobal, disableNoChina, defaultMarkets } = params;
  if (!Array.isArray(markets) || !Array.isArray(countriesMarkets)) {
    return defaultMarkets;
  }
  if (disableNoChina && markets[0] === 'global_wo_china') {
    return defaultMarkets;
  }
  if (disableGlobal && markets[0] === 'global') {
    return defaultMarkets;
  }
  if (['global', 'global_wo_china'].includes(markets[0])) {
    return markets;
  }
  const result = markets.filter((countryCode) => countriesMarkets.includes(countryCode));
  if (result?.length > 0) {
    return result;
  }
  return defaultMarkets;
};

/**
 * 判断当前所选指标列表中是否包含 Ampere 数据源指标
 * 入参：指标 value 数组，支持两种形态：
 *   1. PCConsole 榜单/DataBrain Calibration 的 "<segment>###<metricKey>" 字符串（如 "mau###ampere_mau"）
 *   2. 裸 metricKey（如 "ampere_mau"）
 * 判定：metricKey 以 "ampere_" 开头 或 DATA_SOURCE_METRICS[metricKey].source === 'ampere'
 */
export const containsAmpereMetric = (metricValues?: string[] | null): boolean => {
  if (!Array.isArray(metricValues) || metricValues.length === 0) return false;
  return metricValues.some((raw) => {
    if (!isString(raw) || !raw) return false;
    const metricKey = raw.includes('###') ? raw.split('###')[1] : raw;
    if (!metricKey) return false;
    if (metricKey.startsWith('ampere_')) return true;
    return DATA_SOURCE_METRICS?.[metricKey]?.source === 'ampere';
  });
};

// 特殊处理国家值为交集之后的值
export const formatRegionParams = (markets: string[], countryCode: string[]) => {
  const result = getCountryCodeIntersection({
    markets,
    countriesMarkets: countryCode,
    disableNoChina: true,
    defaultMarkets: [],
  });
  return result;
};

export const isSameDate = (startDate: string, endDate: string, dateType: string) => {
  const dateTypeFormat = changeTimetoDay(dateType);
  const start = moment(startDate).startOf(dateTypeFormat);
  const end = moment(endDate).startOf(dateTypeFormat);
  return start.isSame(end);
};

/** 筛选器继承时获取初始默认继承值 */

export const getFilterExtendDefaultValue = (
  isOperate: boolean,
  filterExtend: any,
  defaultValue: any,
) => {
  // 没有操作过筛选器，不继承
  if (!isOperate) return defaultValue ?? {};
  try {
    return {
      [filterKeyMap.RANGE_TIME]: {
        type:
          filterExtend[filterKeyMap.RANGE_TIME]?.filterType ??
          defaultValue[filterKeyMap.RANGE_TIME]?.type,
        value:
          filterExtend[filterKeyMap.RANGE_TIME]?.filterValue ??
          defaultValue[filterKeyMap.RANGE_TIME]?.value,
      },
      [filterKeyMap.REGION]: {
        regions:
          filterExtend[filterKeyMap.REGION]?.filterRegions ??
          defaultValue[filterKeyMap.REGION]?.regions,
        markets:
          filterExtend[filterKeyMap.REGION]?.filterMarkets ??
          defaultValue[filterKeyMap.REGION]?.markets,
        type:
          filterExtend[filterKeyMap.REGION]?.filterType ?? defaultValue[filterKeyMap.REGION]?.type,
      },
      [filterKeyMap.GENRETOR]: {
        source:
          filterExtend[filterKeyMap.GENRETOR]?.genretor?.source ??
          defaultValue[filterKeyMap.GENRETOR]?.source,
        mainGenres:
          filterExtend[filterKeyMap.GENRETOR]?.genretor?.mainGenres ??
          defaultValue[filterKeyMap.GENRETOR]?.mainGenres,
        subGenres:
          filterExtend[filterKeyMap.GENRETOR]?.genretor?.subGenres ??
          defaultValue[filterKeyMap.GENRETOR]?.subGenres,
      },
      [filterKeyMap.RELEASE_TIME]: {
        value:
          filterExtend[filterKeyMap.RELEASE_TIME]?.filterValue?.value ??
          defaultValue[filterKeyMap.RELEASE_TIME]?.value,
      },
      [filterKeyMap.ALL_COMPANYS]: {
        value:
          filterExtend[filterKeyMap.ALL_COMPANYS]?.value ??
          defaultValue[filterKeyMap.ALL_COMPANYS]?.value,
      },
      [filterKeyMap.ALL_Games]: {
        value:
          filterExtend[filterKeyMap.ALL_Games]?.value ??
          defaultValue[filterKeyMap.ALL_Games]?.value,
        checkedList:
          filterExtend[filterKeyMap.ALL_Games]?.checkedList ??
          defaultValue[filterKeyMap.ALL_Games]?.checkedList,
        keywords:
          filterExtend[filterKeyMap.ALL_Games]?.keywords ??
          defaultValue[filterKeyMap.ALL_Games]?.keywords,
        isSearchAll:
          filterExtend[filterKeyMap.ALL_Games]?.isSearchAll ??
          defaultValue[filterKeyMap.ALL_Games]?.isSearchAll,
      },
      [filterKeyMap.REVENUE_SCOPE]: {
        value: filterExtend[filterKeyMap.REVENUE_SCOPE]?.value ?? defaultValue?.[filterKeyMap.REVENUE_SCOPE]?.value ?? REVENUE_SCOPE_VALUES.EXCLUDING,
      },
    };
  } catch (err) {
    return {};
  }
};

/** 筛选器更新国家列表 */
export const updateInputListRegionOptions = (params: {
  inputList: any[];
  regionOptions: any[];
  getRadioOptionsTips?: Function;
  regionKey: string; // filterKeyMap.REGION
}) => {
  const { inputList, regionOptions, getRadioOptionsTips, regionKey } = params || {};
  if (!Array.isArray(regionOptions) || !Array.isArray(inputList)) {
    return inputList;
  }

  return inputList.map((item) =>
    item?.key === regionKey ? { ...item, options: regionOptions, getRadioOptionsTips } : item,
  );
};

/** 最早时间更新时间筛选器快捷键 */
export const updateInputListRangeTime = (params: {
  inputList: any[];
  key: string;
  earliestDataTime?: string;
  sinceReleaseTime?: string;
}) => {
  const { inputList, key, earliestDataTime, sinceReleaseTime } = params || {};
  if (!Array.isArray(inputList)) {
    return inputList;
  }

  return inputList.map((item) =>
    item?.key === key
      ? {
        ...item,
        earliestDataTime,
        sinceReleaseTime,
      }
      : item,
  );
};

/** 将单粒度时间变成范围保存到继承值里 */
export const changeSingleTimeToRange = (params: {
  timeType: 'month' | 'day'; // 月粒度/天粒度
  subtractNum: number; // 往前推几月/天
  filterExtend: any;
}) => {
  const { timeType, subtractNum, filterExtend } = params || {};
  if (!timeType || !filterExtend) {
    return;
  }

  const timeFilterExtend = filterExtend?.[filterKeyMap.RANGE_TIME];
  const startDate = timeFilterExtend?.start_date || '';

  if (timeFilterExtend && startDate) {
    const adjustedStartDate = moment(startDate).subtract(subtractNum, timeType);
    return {
      ...filterExtend,
      [filterKeyMap.RANGE_TIME]: {
        ...timeFilterExtend,
        filterValue: [adjustedStartDate, timeFilterExtend?.filterValue?.[1]],
        start_date: adjustedStartDate.format('YYYY-MM-DD'),
      },
    };
  }
};

/** 将过滤后的国家筛选器值保存到继承值里 */
const getCoutriesExtendValue = (params: {
  regionType: any;
  marketsValues: any;
  // regionValues: any;
  filterExtend: any;
  regionFilterType: 'common' | 'selected';
}) => {
  const { regionType, marketsValues, filterExtend, regionFilterType } = params || {};
  if (!filterExtend) {
    return;
  }
  if (regionFilterType === 'common') {
    const marketExtend = filterExtend[filterKeyMap.REGION];
    if (marketExtend) {
      const newVal = {
        ...filterExtend,
        [filterKeyMap.REGION]: {
          ...marketExtend,
          filterType: regionType,
          filterRegions: marketsValues,
          filterMarkets: marketsValues,
          region_type: regionType,
          markets: marketsValues,
          realMarkets: marketsValues,
          regions: marketsValues,
          region_values: marketsValues,
        },
      };
      return newVal;
    }
  } else if (regionFilterType === 'selected') {
    const marketExtendComposed = filterExtend?.[filterKeyMap.COMPOSED_COUNTRY_SELECT];
    if (marketExtendComposed) {
      const newVal = {
        ...filterExtend,
        [filterKeyMap.COMPOSED_COUNTRY_SELECT]: {
          value: {
            type: marketsValues?.length > 1 ? 'multiple' : 'single',
            countries: marketsValues,
          },
        },
      };
      return newVal;
    }
  }
  return filterExtend;
};

const getFilterMarkets = (
  markets: string[],
  regionOptions: string[],
  defalutMarket: string[],
  disableNoChina: boolean,
  disableGlobal = false,
) => {
  const countriesCodes = isStringArray(regionOptions)
    ? regionOptions
    : formatRegionOptions(regionOptions)?.countryCode;

  // 保存的方案选择后可特殊处理国家值为交集之后的值
  const result = getCountryCodeIntersection({
    markets,
    countriesMarkets: countriesCodes,
    disableNoChina,
    defaultMarkets: defalutMarket,
    disableGlobal,
  });
  return result;
};
function isStringArray(array: string[]) {
  return array?.every((item) => typeof item === 'string');
}

/** 返回regiontype, markets, 返回更新后的继承值 */
export const getCountriesExtendParams = (params: {
  regionOptions: string[] | any[]; // 国家筛选器options, 也可以是字符串数组
  defalutMarket: string[]; // 没取到交集用的默认值
  disableNoChina: boolean;
  disableGlobal?: boolean;
  // regionType:string,
  markets: string[];
  filterExtend: any;
  regionFilterType: 'common' | 'selected';
}) => {
  const {
    markets,
    regionOptions,
    defalutMarket,
    disableNoChina,
    disableGlobal,
    filterExtend,
    regionFilterType,
  } = params;

  // 不同国家国家取交集,regionOptions为字符串数组时默认为所有国家简称
  const filterMarkets: string[] = getFilterMarkets(
    markets,
    regionOptions,
    defalutMarket,
    disableNoChina,
    disableGlobal,
  );
  // 只有global和globalexeptchina是region,其他发market
  const regionValues = trankMarketsParamsExceptGlobal({
    regions: filterMarkets,
    markets: filterMarkets,
  });
  /** 获取更新后的继承值 */
  const updateCoutriesFilter = getCoutriesExtendValue({
    regionType: regionValues?.regionType,
    marketsValues: regionValues?.regions,
    filterExtend,
    regionFilterType,
  });
  return {
    regionValues,
    filterExtendValue: updateCoutriesFilter,
  };
};

export const handleMetricRangeFilter = (filterData: any) => {
  const newFetchParams: any = {};
  const configList = [];

  const filterSourceMetrics = {
    [filterKeyMap.REVENUE_GROWTH]: {
      type: 'growth',
      source: filterKeyMap.REVENUE_GROWTH,
      metric: 'revenue',
    },

    [filterKeyMap.REVENUE_RANGE]: {
      type: 'range',
      source: filterKeyMap.REVENUE_RANGE,
      metric: 'revenue',
    },
    [filterKeyMap.ARPU_RANGE]: {
      type: 'range',
      source: 'arpuRange',
      metric: 'arpu',
    },
    [filterKeyMap.ACTIVE_USERS_RANGE]: {
      type: 'range',
      source: 'activeUsersRange',
      metric: 'au',
    },
    [filterKeyMap.DOWNLOADS_RANGE]: {
      type: 'range',
      source: 'downloadsRange',
      metric: 'download',
    },
    [filterKeyMap.RPD_RANGE]: {
      type: 'range',
      source: 'rpdRange',
      metric: 'sensortower__rpd',
    },
    ...filterKeyMap.buildMetricRangeSourceMap(filterKeyMap.RETENTION_D_RANGE_DEFS),
    [filterKeyMap.PCU_RANGE]: {
      type: 'range',
      source: 'source_steam',
      metric: 'source_steam__pcu',
    },
    [filterKeyMap.WISHLISTS_RANGE]: {
      type: 'range',
      source: 'gamalytic',
      metric: 'gamalytic__wishlists_total',
    },
    [filterKeyMap.DIGITAL_RANGE]: {
      type: 'range',
      source: 'mscience',
      metric: 'digitalRevenue',
    },
    [filterKeyMap.PHYSICAL_RANGE]: {
      type: 'range',
      source: 'npd',
      metric: 'revenue',
    },
    [filterKeyMap.PRICE_RANGE]: {
      type: 'range',
      source: 'alinea',
      metric: 'alinea__est_price',
    },
    [filterKeyMap.PREMIUM_RANGE]: {
      type: 'range',
      source: 'alinea',
      metric: 'alinea__est_revenue',
    },
  } as any;

  for (const key of Object.keys(filterSourceMetrics)) {
    const filterValue = filterData?.[key]?.value;
    if (filterValue) {
      configList.push({ ...filterSourceMetrics[key], value: filterValue });
    }
  }

  const metricFilter = configList.reduce((result, item) => {
    const config: any[] = [];
    if (item.type === 'range') {
      if (!isEmpty(item?.value?.min)) {
        config.push({
          metric: item.metric,
          opt: '>=',
          value: item?.value?.min,
        });
      }
      if (!isEmpty(item?.value?.max)) {
        config.push({ metric: item.metric, opt: '<', value: item?.value?.max });
      }
    }
    if (item.type === 'growth') {
      if (!isEmpty(item?.value?.value) && !isEmpty(item?.value?.opt)) {
        config.push({
          metric: item.metric,
          opt: item?.value?.opt,
          value: item?.value?.value,
          ratio_type: 1,
        });
      }
    }
    result[item.source] = (result[item.source] || []).concat(config);
    return result;
  }, {} as Record<string, any[]>);

  const shouldResetVgiMetrics = configList.some((item) =>
    ['gamalytic__est_price', 'gamalytic__est_revenue'].includes(item.metric),
  );
  const shouldResetSteamchartsMetrics = configList.some(
    (item) => item.metric === 'source_steam__pcu',
  );
  if (shouldResetVgiMetrics) {
    metricFilter.vginsights = [];
  }
  if (shouldResetSteamchartsMetrics) {
    metricFilter.steamcharts = [];
  }

  if (Object.keys(metricFilter).length) {
    newFetchParams.metric_filter = metricFilter;
  }
  return newFetchParams;
};

const deviceFilterKeys: string[] = [
  filterKeyMap.METRICTABLE_DEVICE,
  filterKeyMap.DIGITAL_DEVICE,
  filterKeyMap.LIFETIME_DEVICE,
  filterKeyMap.MAU_DEVICE,
  filterKeyMap.PHYSICAL_DEVICE,
  filterKeyMap.GSD_DEVICE,
];
export const handleDeviceFilter = (filterData: any, filterKeyList: string[]) => {
  const deviceKey: string = filterKeyList?.filter((value) => deviceFilterKeys.includes(value))[0];
  const device = filterData?.[deviceKey];

  return { device: device?.value };

  // let deviceObj: any = {};
  // deviceFilterKeys.forEach(key => {
  //   const device = filterData?.[key];
  //   if (device) {
  //     deviceObj[key] = device.value;
  //   }
  // });
  // return deviceObj;
};

export const getFilterValueParams = (
  filterData: any,
  specialParams: SpecialParamsType,
  filterKeyList: string[],
) => {
  let updateFilterData = filterData;
  const newFetchParams: any = {};
  const genreParams = filterData?.[filterKeyMap.GENRETOR];
  const genreWithSegmentParams = filterData?.[filterKeyMap.GENRETOR_WITH_SEGMENT];
  const releaseTimeExtend = filterData?.[filterKeyMap.RELEASE_TIME];
  if (genreParams) {
    Object.assign(newFetchParams, handleGenreFilterParams(genreParams));
  }

  /**
   * 增加带Segment快捷切换品类数据源的组合品类筛选器组件的value处理
   */
  if (genreWithSegmentParams) {
    Object.assign(newFetchParams, handleGenreFilterParams(genreWithSegmentParams));
  }

  if (releaseTimeExtend) {
    Object.assign(newFetchParams, handleReleaseTimeFilterParams(releaseTimeExtend));
  }

  const companyExtend = filterData?.[filterKeyMap.ALL_COMPANYS];
  if (companyExtend) {
    Object.assign(newFetchParams, { company: companyExtend?.value || [] });
  }

  const steamTags = filterData?.[filterKeyMap.STEAM_TAGS];
  if (steamTags) {
    Object.assign(newFetchParams, {
      [filterKeyMap.STEAM_TAGS]: steamTags?.value || [],
    });
  }

  const gameMode = filterData?.[filterKeyMap.GAME_MODE];
  if (gameMode) {
    Object.assign(newFetchParams, {
      [filterKeyMap.GAME_MODE]: gameMode?.value || [],
    });
  }

  const gameEngine = filterData?.[filterKeyMap.GAME_ENGINE];
  if (gameEngine) {
    Object.assign(newFetchParams, {
      game_engine: gameEngine?.value || [],
      game_engine_relation: gameEngine?.logic || 'OR',
    });
  }

  const companyExtendGame = filterData?.[filterKeyMap.ALL_Games];
  if (companyExtendGame) {
    Object.assign(newFetchParams, {
      filter_ids: companyExtendGame?.value || [],
      keywords: companyExtendGame?.keywords,
      isSearchAll: companyExtendGame?.isSearchAll,
      checkedList: companyExtendGame?.checkedList,
      entityTypesIdsObj: handleAllGameFilter(companyExtendGame?.checkedList),
    });
  }

  const newGameTimeExtend = filterData?.[filterKeyMap.DAY_RANGE_TIME];
  if (newGameTimeExtend) {
    const timeObj = handleCommonTimeFilterParams(newGameTimeExtend, FilterTimeType.rangeDay);
    Object.assign(newFetchParams, timeObj?.filterParams);
    updateFilterData = {
      ...updateFilterData,
      [filterKeyMap.DAY_RANGE_TIME]: timeObj?.filterValues,
    };
  }

  const timeExtend = filterData?.[filterKeyMap.RANGE_TIME];
  if (timeExtend) {
    const timeObj = handleCommonTimeFilterParams(
      timeExtend,
      specialParams?.rangeTimeType || FilterTimeType.commonRange,
    );
    Object.assign(newFetchParams, timeObj?.filterParams);
    updateFilterData = {
      ...updateFilterData,
      [filterKeyMap.RANGE_TIME]: timeObj?.filterValues,
    };
  }
  const metricRange = handleMetricRangeFilter(filterData);
  Object.assign(newFetchParams, metricRange);

  const deviceObj = handleDeviceFilter(filterData, filterKeyList);
  Object.assign(newFetchParams, deviceObj);

  // 国家筛选器应该做完交集后再返回
  const regionExtend = filterKeyList?.includes(filterKeyMap.REGION)
    ? filterData?.[filterKeyMap.REGION]
    : null;
  if (regionExtend) {
    // 不同国家国家取交集,并且把交集的结果重新赋值到继承值中
    const { regionValues, filterExtendValue } = getCountriesExtendParams({
      regionOptions: specialParams?.regionOptions || [], // 国家筛选器options,
      defalutMarket: specialParams?.defalutMarket || [], // 没取到交集用的默认值
      disableNoChina: specialParams?.disableNoChina || false,
      markets: regionExtend?.markets,
      filterExtend: updateFilterData,
      regionFilterType: 'common', // 级联筛选器
    });
    updateFilterData = filterExtendValue;

    Object.assign(newFetchParams, {
      regions: regionValues?.regions,
      region_type: regionValues?.regionType,
    });
  }
  // 国家筛选器应该做完交集后再返回
  const composedCountryExtend = filterKeyList?.includes(filterKeyMap.COMPOSED_COUNTRY_SELECT)
    ? filterData?.[filterKeyMap.COMPOSED_COUNTRY_SELECT]
    : null;
  if (composedCountryExtend) {
    const markets = composedCountryExtend?.value?.countries;
    const marketsLen = markets?.length;

    // 不同国家国家取交集,并且把交集的结果重新赋值到继承值中
    const { regionValues, filterExtendValue } = getCountriesExtendParams({
      regionOptions: specialParams?.regionOptions || [], // 国家筛选器options,
      defalutMarket: specialParams?.defalutMarket || [], // 没取到交集用的默认值
      disableGlobal: specialParams?.disableGlobal || false,
      disableNoChina: specialParams?.disableNoChina || true,
      markets:
        specialParams?.isSimpleCountry && (!marketsLen || marketsLen > 1)
          ? specialParams?.defalutMarket
          : markets,
      filterExtend: updateFilterData,
      regionFilterType: 'selected', // 下拉国家筛选器
    });

    updateFilterData = filterExtendValue;
    Object.assign(newFetchParams, {
      regions: regionValues?.regions,
      region_type: regionValues?.regionType,
    });
  }

  const segmentExtend = filterData?.[filterKeyMap.DIGITAL_SEGEMENT];

  if (segmentExtend) {
    Object.assign(newFetchParams, { segment: segmentExtend?.value });
  }

  const publisher = filterData?.[filterKeyMap.PUBLISHER];

  if (publisher) {
    Object.assign(newFetchParams, {
      gsd_types: publisher?.value === 'all' ? [] : [publisher?.value],
    });
  }

  const segmentMultipleExtend = filterData?.[filterKeyMap.DIGITAL_SEGMENT_MULTIPLE];

  if (segmentMultipleExtend) {
    Object.assign(newFetchParams, {
      segment_list: segmentMultipleExtend?.value,
    });
  }

  const gsdSegmentExtend = filterData?.[filterKeyMap.GSD_SEGEMENT];

  if (gsdSegmentExtend) {
    Object.assign(newFetchParams, { gsdSegment: gsdSegmentExtend?.value });
  }

  // Following的分组筛选器
  const groupExtend = filterData?.[filterKeyMap.FOLLOWING_GLOUP];
  if (groupExtend) {
    Object.assign(newFetchParams, { group_name: groupExtend?.value });
  }

  const sensorTowerValue = handleSensorTowerValueFilter(filterData);
  Object.assign(newFetchParams, sensorTowerValue);

  // 处理 App Magic Tags
  const appMagicValue = handleAppMagicValueFilter(filterData);
  Object.assign(newFetchParams, appMagicValue);

  const platfromExtend = filterData?.[filterKeyMap.PLATFORM];
  if (platfromExtend) {
    Object.assign(newFetchParams, { platforms: platfromExtend?.value });
  }

  const platformCascaderExtend = filterData?.[filterKeyMap.PLATFORMCASCADER];
  if (platformCascaderExtend) {
    Object.assign(newFetchParams, {
      platform_cascader: platformCascaderExtend?.value || {},
    });
  }

  const kolPlatfromExtend = filterData?.[filterKeyMap.KOL_PLATFORM];
  if (kolPlatfromExtend) {
    Object.assign(newFetchParams, { kolPlatforms: kolPlatfromExtend?.value });
  }

  const dataSourceExtend = filterData?.[filterKeyMap.DATASOURCE];
  if (dataSourceExtend) {
    let dataSourceValue = dataSourceExtend?.value;
    // 归一化：确保 data_sources 始终为数组（Demographics 单选模式下 AnnieSource 返回字符串）
    if (typeof dataSourceValue === 'string') {
      dataSourceValue = [dataSourceValue];
    }
    if (Array.isArray(dataSourceValue) && !specialParams?.dataSourceShowDatabrain) {
      dataSourceValue = dataSourceValue.filter((t: string) => t !== 'ogdb');
    }
    Object.assign(newFetchParams, { data_sources: dataSourceValue });
  }
  const pheatRangExtend = filterData?.[filterKeyMap.PHEAD_RANG];
  if (pheatRangExtend) {
    Object.assign(newFetchParams, { pheat_range: pheatRangExtend?.value });
  }
  const planExtend = filterData?.[filterKeyMap.PLAN];
  if (planExtend) {
    Object.assign(newFetchParams, { plan: planExtend?.value });
  }

  const competitorPlatformExtend = filterData?.[filterKeyMap.COMPETITOR_PLATFORM];
  if (competitorPlatformExtend) {
    Object.assign(newFetchParams, {
      competitor_platform: competitorPlatformExtend?.value,
    });
  }

  const streamintGameTypeExtend = filterData?.[filterKeyMap.STREAMING_GAME_TYPE];
  if (streamintGameTypeExtend) {
    Object.assign(newFetchParams, {
      streaming_gametype: streamintGameTypeExtend?.value,
    });
  }

  const companiesGameTypeExtend = filterData?.[filterKeyMap.GAME_TYPE];
  if (companiesGameTypeExtend) {
    Object.assign(newFetchParams, {
      companies_gametype: companiesGameTypeExtend?.value,
    });
  }

  const ipoStatusExtend = filterData?.[filterKeyMap.IPO_STATUS];
  if (ipoStatusExtend) {
    Object.assign(newFetchParams, {
      companies_ipostatus: ipoStatusExtend?.value,
    });
  }

  const headQuatersExtend = filterData?.[filterKeyMap.COUNTRY];
  if (headQuatersExtend) {
    Object.assign(newFetchParams, {
      companies_headquaters: headQuatersExtend?.value,
    });
  }

  const revenueRangeUnitExtend = filterData?.[filterKeyMap.REVENUE_RANGE_UNIT];
  if (revenueRangeUnitExtend) {
    Object.assign(newFetchParams, {
      companies_revenuerange: revenueRangeUnitExtend?.value,
    });
  }
  const revenueScopeExtend = filterData?.[filterKeyMap.REVENUE_SCOPE];
  if (revenueScopeExtend) {
    Object.assign(newFetchParams, {
      revenue_scope: revenueScopeExtend?.value,
    });
  }
  const dataSourceSimExtend = filterData?.[filterKeyMap.DATASOURCESIM];
  if (dataSourceSimExtend) {
    Object.assign(newFetchParams, {
      datasource_sim: dataSourceSimExtend?.value,
    });
  }
  const retentionMetricsExtend = filterData?.[filterKeyMap.RETENTION];
  if (retentionMetricsExtend) {
    Object.assign(newFetchParams, {
      retention_metrics: retentionMetricsExtend?.value,
    });
  }
  const appSelectorExtend = filterData?.[filterKeyMap.APP_SELECTOR];
  if (appSelectorExtend) {
    Object.assign(newFetchParams, { app_ids: appSelectorExtend?.value });
  }
  const streamingPlatformExtend = filterData?.[filterKeyMap.STREAMING_PLATFORM];
  if (streamingPlatformExtend) {
    Object.assign(newFetchParams, {
      streaming_platform: streamingPlatformExtend?.value,
    });
  }

  // KPITrends数据源筛选器
  const metricForKPITrendsExtend = filterData?.[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND];
  if (metricForKPITrendsExtend) {
    Object.assign(newFetchParams, {
      metric_selection_for_trends: metricForKPITrendsExtend?.value,
    });
  }

  // 数据源筛选
  // const dataSourceSim = filterData?.[filterKeyMap.DATASOURCESIM];
  // if (dataSourceSim) {
  //   Object.assign(newFetchParams, { dataSourceSingle: dataSourceSim?.value });
  // }

  if (!!filterData) {
    if (Reflect.has(filterData, filterKeyMap.MOBILE_OVERLAP)) {
      Object.assign(newFetchParams, {
        [filterKeyMap.MOBILE_OVERLAP]: filterData?.[filterKeyMap.MOBILE_OVERLAP]?.value,
      });
    }
  }

  return { params: newFetchParams, updateFilterData };
};

// 过滤筛选器列表为目前页面有的筛选器，没有的筛选器过滤掉
export const filteredObject = (dataObj: any, filterKeys: string[]) => {
  if (!Array.isArray(filterKeys) || typeof dataObj !== 'object' || dataObj === null) {
    return {};
  }
  return Object.keys(dataObj)
    .filter((key) => filterKeys.includes(key))
    .reduce((obj: any, key) => {
      obj[key] = dataObj[key];
      return obj;
    }, {});
};

// export const getFilterKeyListHasCountry = (listKey: string[]) => {
//   if (!Array.isArray(listKey)) return [];
//   if (listKey?.includes(filterKeyMap.COMPOSED_COUNTRY_SELECT)) {
//     return [...listKey, filterKeyMap.REGION];
//   } if (listKey?.includes(filterKeyMap.REGION)) {
//     return [...listKey, filterKeyMap.COMPOSED_COUNTRY_SELECT];
//   }
//   return listKey;
// };

/** 情报页面的筛选器入口：
 * 1 从url获取的参数（高优先级-目前nikke链接会用到筛选器转换的值如region=jp）
 * 2 从短链接获取的参数（高优先级）
 * 3 从保存筛选器里获取的参数（中优先级）
 * 4 从继承里拿的筛选器（低优先级)
 * 返回筛选器值和对应的参数
 */
export const getDbiFilterParams = (params: {
  userInfo?: { userid: string };
  // 筛选器保存的值
  filterSaveData: any;
  // 筛选器继承值
  filterExtend: any;
  // 筛选器特殊配置，如时间类型，因家类型等
  specialParams?: SpecialParamsType;
  // 是否是继承模式
  isOperate: boolean;
  // 是否是保存模式
  filterName?: string | null;
  // 当前的筛选器列表string[]
  filterKeyList: string[];
  // url短链接参数
  urlCustomParams?: any;
  // 短链接保存的筛选器值
  urlShortLinkFilter?: any;
  genreOptionsMap?: any;
}) => {
  const {
    userInfo,
    isOperate,
    filterExtend,
    filterName,
    filterSaveData,
    filterKeyList,
    specialParams = {},
    urlCustomParams = {},
    urlShortLinkFilter = {},
    genreOptionsMap,
  } = params;
  let filterValue = {} as any;
  let newFetchParams = {} as any;

  try {
    // 先更新筛选器展示--须等Loading结束，不然国家筛选器列表没拿到
    const urlFilter: any = getDbiUrlParams(history, { genreOptionsMap });
    // 是否有筛选器参数用于url； nikke的链接有用到
    const hasUrlFilter = typeof urlFilter === 'object' ? Object.keys(urlFilter)?.length : false;
    // 是否有url短链接或URL参数筛选器
    const hasUrlShortLinkVal = history?.location?.query?.[URL_SHORT_LINK_NAME] || (urlShortLinkFilter && Object.keys(urlShortLinkFilter).length > 0);
    // 筛选器优先级按数组顺序：
    const filterSources = [
      // 之前nikke方案有筛选器生成url的功能，需要读取到筛选器中  因为company参与短链接分享
      { condition: !!hasUrlFilter, filterSource: urlFilter, hasCompany: true },
      // 短链接筛选器功能 或 URL参数筛选器功能  因为company参与短链接分享
      {
        condition: !!hasUrlShortLinkVal,
        filterSource: urlShortLinkFilter,
        hasCompany: true,
      },
      // 保存筛选器功能  因为company每个榜单不一样，所以不参与保存
      {
        condition: !!filterName,
        filterSource: filterSaveData,
        hasCompany: false,
      },
      // 继承筛选器功能  因为company每个榜单不一样，所以不参与保存
      { condition: !!isOperate, filterSource: filterExtend, hasCompany: false },
    ];
    // // 国家筛选器因继承关系，会同时使其存在不被过滤掉
    // const newFilterKeyList: string[] = getFilterKeyListHasCountry(filterKeyList);
    // console.debug('filterSourcesfilterSources~~~', filterSources);
    for (const source of filterSources) {
      if (source.condition) {

        const validFilter = filteredObject(source.filterSource, filterKeyList);
        // console.debug('filterSourcesfilterSources~~~validFilter', validFilter);
        const { params: newParams, updateFilterData } = getFilterValueParams(
          validFilter,
          specialParams,
          filterKeyList,
        );
        // 公司筛选器值没有继承,在此处加isOperate判断: 不继承的情况下是筛选器点击的值
        const hasCompanyExtendValue = !source.hasCompany;
        const companyValue = hasCompanyExtendValue ? undefined : newParams?.company;
        // url上面的参数修正后传过来
        newFetchParams = {
          ...newFetchParams,
          ...newParams,
          ...urlCustomParams,
          company: companyValue,
        };
        filterValue = Object.assign(
          updateFilterData,
          hasCompanyExtendValue ? { [filterKeyMap.ALL_COMPANYS]: { value: [] } } : {},
        );
        // 如果当前是有短链接 token（filters_key），则清除保存筛选器的值
        // 注意：URL 参数筛选器（如 product_model）不清除保存的筛选器
        const hasShortLinkToken = history?.location?.query?.[URL_SHORT_LINK_NAME];
        if (hasShortLinkToken) {
          if (userInfo?.userid) {
            saveFilterName(userInfo, '');
          }
          filterValue = {
            ...filterValue,
            [filterKeyMap.MY_FILTER]: {
              value: '',
            },
          };
        } else if (!!filterName && filterSaveData) {
          // 修复：如果使用的是保存的筛选方案，确保 MY_FILTER 的值也被正确恢复
          // 这样可以保证刷新页面后 My Filter 筛选器能正确显示选中的方案
          filterValue = {
            ...filterValue,
            [filterKeyMap.MY_FILTER]: {
              value: filterName,
            },
          };
        }
        break;
      }
    }
  } catch (err) {
    console.log(err);
  }
  return {
    // 筛选器的赋值
    filterValue,
    // 筛选器值处理为object，用于转换为接口参数
    filterParams: newFetchParams,
  };
};

// 是否能正常转换moment格式时间,错误的时间格式或者空，返回false
export const isValidDate = (date: string) => {
  if (date === '') return false;
  return date && moment(date).isValid();
};

// 判断对象 ‌B‌ 是否包含对象 ‌A‌ 中的所有属性
export const hasAllProperties = (objA: any, objB: any) => {
  // 确保 objB 是有效对象
  if (typeof objA !== 'object' || objA === null) return false;
  if (typeof objB !== 'object' || objB === null) return false;
  // 检查 objA 的所有属性是否都在 objB 中存在
  return Object.keys(objA)?.every((key) => Object.prototype.hasOwnProperty.call(objB, key));
};

// export const hasAllTrueProperties = (objA: any, objB: any) => {
//   // 确保 objB 是有效对象
//   if (typeof objB !== 'object' || objB === null) return false;
//   // 检查 objA 的所有属性是否在 objB 中存在且值为 false
//   return Object.keys(objA)?.every(
//     (key) => Object.prototype.hasOwnProperty.call(objB, key) && objB[key] === true, // 严格检查值是否为 false
//   );
// };
